import { supabase } from '../lib/supabase';
import { SavedDiagnostic } from './diagnosticService';

export interface DiagnosticWithClient extends SavedDiagnostic {
  client_email: string | null;
  client_last_sign_in: string | null;
}

/** Diagnostic enrichi pour la vue company admin (advisor + client status) */
export interface CompanyDiagnosticRow extends SavedDiagnostic {
  advisor_first_name: string | null;
  advisor_last_name: string | null;
  advisor_email: string | null;
  client_last_sign_in: string | null;
}

export interface ResendInvitationResult {
  success: boolean;
  error?: string;
}

// ── Types métriques commerciales ───────────────────────────────────────────────

export interface AdvisorStatsResult {
  total_diagnostics: number;
  total_actions_completed: number;
  total_revenue: number;
}

/** Retourné par getCompanyAdvisorsStats — remplace AdminUserProfile dans la liste */
export interface AdvisorStatsRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  is_active: boolean;
  total_diagnostics: number;
  total_actions_completed: number;
  total_revenue: number;
}

export interface CompanyRevenueResult {
  total_actions_completed: number;
  total_revenue: number;
}

export interface DiagnosticRevenueEntry {
  diagnostic_id: string;
  actions_completed: number;
  revenue: number;
}

export interface Company {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CompanyStats {
  adminId: string | null;
  adminName: string | null;
  adminEmail: string | null;
  advisorCount: number;
  diagnosticCount: number;
  activeClientCount: number;
  totalRevenue: number;
  totalActionsCompleted: number;
}

export interface CompanyWithStats extends Company {
  stats: CompanyStats;
}

export interface AdminUserProfile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  company_id: string;
  is_active: boolean;
  created_at: string;
}

// ── Super Admin ────────────────────────────────────────────────────────────────

export async function getAllCompanies(): Promise<Company[]> {
  const { data, error } = await supabase
    .from('companies')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Récupère toutes les entreprises avec leurs stats agrégées
 * en 4 requêtes parallèles (pas de N+1).
 */
export async function getAllCompaniesWithStats(): Promise<CompanyWithStats[]> {
  const [companiesRes, adminsRes, allUsersRes, diagnosticsRes, revenueRes] = await Promise.all([
    supabase.from('companies').select('*').order('created_at', { ascending: false }),
    supabase.from('users').select('id, email, first_name, last_name, company_id').eq('role', 'company_admin'),
    // Tous les users pour pouvoir résoudre company_id via user_id sur les diagnostics
    supabase.from('users').select('id, company_id, role'),
    // user_id inclus pour fallback si company_id absent sur le diagnostic
    supabase.from('diagnostics').select('company_id, client_id, user_id'),
    // CA consolidé par entreprise en une seule requête
    supabase.rpc('get_all_companies_revenue'),
  ]);

  if (companiesRes.error) throw new Error(companiesRes.error.message);

  const companies: Company[] = companiesRes.data ?? [];
  const admins = adminsRes.data ?? [];
  const allUsers = allUsersRes.data ?? [];
  const diagnostics = diagnosticsRes.data ?? [];

  // Indexation du CA par company_id
  const revenueMap = new Map<string, { revenue: number; actionsCompleted: number }>();
  (revenueRes.data ?? []).forEach((r: any) => {
    revenueMap.set(r.company_id, {
      revenue: Number(r.total_revenue ?? 0),
      actionsCompleted: Number(r.total_actions_completed ?? 0),
    });
  });

  // Indexation des users par id (pour résolution company via user_id)
  const userMap = new Map<string, { company_id: string | null; role: string }>();
  allUsers.forEach(u => userMap.set(u.id, { company_id: u.company_id, role: u.role }));

  // Indexation de l'admin par company_id
  const adminMap = new Map<string, typeof admins[0]>();
  admins.forEach(a => { if (!adminMap.has(a.company_id)) adminMap.set(a.company_id, a); });

  // Comptage des advisors par company
  const advisorCountMap = new Map<string, number>();
  allUsers.filter(u => u.role === 'advisor').forEach(u => {
    if (u.company_id) advisorCountMap.set(u.company_id, (advisorCountMap.get(u.company_id) ?? 0) + 1);
  });

  // Comptage diagnostics + clients — company résolue en priorité depuis diagnostic.company_id,
  // avec fallback sur la company de l'advisor (user_id) pour les diagnostics anciens
  const diagCountMap = new Map<string, number>();
  const clientsPerCompany = new Map<string, Set<string>>();
  diagnostics.forEach(d => {
    const companyId = d.company_id ?? (d.user_id ? userMap.get(d.user_id)?.company_id : null);
    if (!companyId) return;
    diagCountMap.set(companyId, (diagCountMap.get(companyId) ?? 0) + 1);
    if (d.client_id) {
      if (!clientsPerCompany.has(companyId)) clientsPerCompany.set(companyId, new Set());
      clientsPerCompany.get(companyId)!.add(d.client_id);
    }
  });

  return companies.map(company => {
    const admin = adminMap.get(company.id);
    return {
      ...company,
      stats: {
        adminId: admin?.id ?? null,
        adminName: admin ? `${admin.first_name ?? ''} ${admin.last_name ?? ''}`.trim() || null : null,
        adminEmail: admin?.email ?? null,
        advisorCount: advisorCountMap.get(company.id) ?? 0,
        diagnosticCount: diagCountMap.get(company.id) ?? 0,
        activeClientCount: clientsPerCompany.get(company.id)?.size ?? 0,
        totalRevenue: revenueMap.get(company.id)?.revenue ?? 0,
        totalActionsCompleted: revenueMap.get(company.id)?.actionsCompleted ?? 0,
      },
    };
  });
}

export async function createCompany(name: string, slug: string): Promise<Company> {
  const { data, error } = await supabase
    .from('companies')
    .insert({ name, slug, is_active: true })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function toggleCompanyActive(companyId: string, isActive: boolean): Promise<void> {
  const { error } = await supabase
    .from('companies')
    .update({ is_active: isActive })
    .eq('id', companyId);
  if (error) throw new Error(error.message);
}

export async function createCompanyAdmin(
  email: string,
  firstName: string,
  lastName: string,
  companyId: string
): Promise<void> {
  const { data, error } = await supabase.functions.invoke('create-user', {
    body: { email, firstName, lastName, companyId, role: 'company_admin' },
  });
  if (error || data?.error) throw new Error(data?.error ?? error?.message ?? 'Erreur création admin');
}

export async function updateCompanyContactEmail(
  companyId: string,
  email: string
): Promise<void> {
  const { error } = await supabase
    .from('companies')
    .update({ contact_email: email })
    .eq('id', companyId);
  if (error) throw new Error(error.message);
}

// ── Company Admin ──────────────────────────────────────────────────────────────

export async function getCompanyAdvisors(companyId: string): Promise<AdminUserProfile[]> {
  const { data, error } = await supabase
    .from('users')
    .select('id, email, first_name, last_name, role, company_id, is_active, created_at')
    .eq('company_id', companyId)
    .eq('role', 'advisor')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function createAdvisor(
  email: string,
  firstName: string,
  lastName: string,
  companyId: string
): Promise<void> {
  const { data, error } = await supabase.functions.invoke('create-user', {
    body: { email, firstName, lastName, companyId, role: 'advisor' },
  });
  if (error || data?.error) throw new Error(data?.error ?? error?.message ?? 'Erreur création advisor');
}

export async function suspendAdvisor(advisorId: string): Promise<void> {
  const { error } = await supabase
    .from('users')
    .update({ is_active: false })
    .eq('id', advisorId)
    .eq('role', 'advisor');
  if (error) throw new Error(error.message);
}

export async function reactivateAdvisor(advisorId: string): Promise<void> {
  const { error } = await supabase
    .from('users')
    .update({ is_active: true })
    .eq('id', advisorId)
    .eq('role', 'advisor');
  if (error) throw new Error(error.message);
}

export async function reassignDiagnostics(fromAdvisorId: string, toAdvisorId: string): Promise<void> {
  const { error } = await supabase
    .from('diagnostics')
    .update({ user_id: toAdvisorId })
    .eq('user_id', fromAdvisorId);
  if (error) throw new Error(error.message);
}

export async function getCompanyDiagnostics(companyId: string): Promise<CompanyDiagnosticRow[]> {
  const { data: diags, error } = await supabase
    .from('diagnostics')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  const diagnostics = diags ?? [];
  if (diagnostics.length === 0) return [];

  // Advisor info (prénom, nom, email) indexé par user_id
  const advisorIds = [...new Set(
    diagnostics.map(d => d.user_id).filter((id): id is string => !!id)
  )];
  const advisorMap = new Map<string, { first_name: string | null; last_name: string | null; email: string | null }>();
  if (advisorIds.length > 0) {
    const { data: advisors } = await supabase
      .from('users')
      .select('id, first_name, last_name, email')
      .in('id', advisorIds);
    (advisors ?? []).forEach(a =>
      advisorMap.set(a.id, { first_name: a.first_name, last_name: a.last_name, email: a.email })
    );
  }

  // last_sign_in_at des clients via RPC SECURITY DEFINER
  const clientIds = [...new Set(
    diagnostics.map(d => d.client_id).filter((id): id is string => !!id)
  )];
  const signInMap = new Map<string, string | null>();
  if (clientIds.length > 0) {
    const { data: signInData } = await supabase.rpc('get_clients_last_sign_in', {
      p_client_ids: clientIds,
    });
    ((signInData ?? []) as { user_id: string; last_sign_in_at: string | null }[]).forEach(r =>
      signInMap.set(r.user_id, r.last_sign_in_at)
    );
  }

  return diagnostics.map(d => ({
    ...d,
    advisor_first_name: d.user_id ? (advisorMap.get(d.user_id)?.first_name ?? null) : null,
    advisor_last_name:  d.user_id ? (advisorMap.get(d.user_id)?.last_name  ?? null) : null,
    advisor_email:      d.user_id ? (advisorMap.get(d.user_id)?.email       ?? null) : null,
    client_last_sign_in: d.client_id ? (signInMap.get(d.client_id) ?? null) : null,
  }));
}

// ── Métriques commerciales ────────────────────────────────────────────────────

export async function getAdvisorStats(advisorId: string): Promise<AdvisorStatsResult> {
  const { data, error } = await supabase.rpc('get_advisor_stats', { p_advisor_id: advisorId });
  if (error) throw new Error(error.message);
  const row = (data as any[])[0] ?? {};
  return {
    total_diagnostics:       Number(row.total_diagnostics       ?? 0),
    total_actions_completed: Number(row.total_actions_completed ?? 0),
    total_revenue:           Number(row.total_revenue           ?? 0),
  };
}

export async function getCompanyAdvisorsStats(companyId: string): Promise<AdvisorStatsRow[]> {
  const { data, error } = await supabase.rpc('get_company_advisors_stats', { p_company_id: companyId });
  if (error) throw new Error(error.message);
  return ((data as any[]) ?? []).map(r => ({
    id:                      r.id,
    first_name:              r.first_name ?? null,
    last_name:               r.last_name  ?? null,
    email:                   r.email,
    is_active:               r.is_active,
    total_diagnostics:       Number(r.total_diagnostics       ?? 0),
    total_actions_completed: Number(r.total_actions_completed ?? 0),
    total_revenue:           Number(r.total_revenue           ?? 0),
  }));
}

export async function getCompanyRevenue(companyId: string): Promise<CompanyRevenueResult> {
  const { data, error } = await supabase.rpc('get_company_revenue', { p_company_id: companyId });
  if (error) throw new Error(error.message);
  const row = (data as any[])[0] ?? {};
  return {
    total_actions_completed: Number(row.total_actions_completed ?? 0),
    total_revenue:           Number(row.total_revenue           ?? 0),
  };
}

export async function getMultipleDiagnosticsRevenue(
  diagnosticIds: string[]
): Promise<DiagnosticRevenueEntry[]> {
  if (diagnosticIds.length === 0) return [];
  const { data, error } = await supabase.rpc('get_multiple_diagnostics_revenue', {
    p_diagnostic_ids: diagnosticIds,
  });
  if (error) throw new Error(error.message);
  return ((data as any[]) ?? []).map(r => ({
    diagnostic_id:      r.diagnostic_id,
    actions_completed:  Number(r.actions_completed ?? 0),
    revenue:            Number(r.revenue           ?? 0),
  }));
}

// ── Advisor ───────────────────────────────────────────────────────────────────

export async function getAdvisorDiagnostics(advisorId: string): Promise<DiagnosticWithClient[]> {
  // 1. Diagnostics où l'advisor est auteur OU advisor_id désigné
  const { data: diags, error } = await supabase
    .from('diagnostics')
    .select('*')
    .or(`user_id.eq.${advisorId},advisor_id.eq.${advisorId}`)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);

  const diagnostics = diags ?? [];

  const clientIds = [
    ...new Set(
      diagnostics.map(d => d.client_id).filter((id): id is string => !!id)
    ),
  ];

  if (clientIds.length === 0) {
    return diagnostics.map(d => ({ ...d, client_email: null, client_last_sign_in: null }));
  }

  // 2. Emails depuis public.users
  const { data: clientUsers } = await supabase
    .from('users')
    .select('id, email')
    .in('id', clientIds);

  const emailMap = new Map((clientUsers ?? []).map(u => [u.id, u.email as string]));

  // 3. last_sign_in_at depuis auth.users via RPC SECURITY DEFINER
  const { data: signInData } = await supabase.rpc('get_clients_last_sign_in', {
    p_client_ids: clientIds,
  });

  const signInMap = new Map(
    ((signInData ?? []) as { user_id: string; last_sign_in_at: string | null }[]).map(
      r => [r.user_id, r.last_sign_in_at]
    )
  );

  return diagnostics.map(d => ({
    ...d,
    client_email: d.client_id ? (emailMap.get(d.client_id) ?? null) : null,
    client_last_sign_in: d.client_id ? (signInMap.get(d.client_id) ?? null) : null,
  }));
}

export async function deleteDiagnostic(diagnosticId: string): Promise<void> {
  const { error } = await supabase
    .from('diagnostics')
    .delete()
    .eq('id', diagnosticId);
  if (error) throw new Error(error.message);
}

export async function resendInvitation(
  diagnosticId: string,
  email: string,
  advisorId: string,
  companyId: string
): Promise<ResendInvitationResult> {
  try {
    const { data, error } = await supabase.functions.invoke('invite-client', {
      body: {
        email,
        diagnosticId,
        advisorId,
        companyId,
        resend: true,
        redirectTo: `${window.location.origin}/update-password`,
      },
    });

    if (error) return { success: false, error: error.message };
    if (!data?.success) return { success: false, error: data?.error || 'Échec de la réinvitation' };

    // Mettre à jour invitation_sent_at localement
    await supabase
      .from('diagnostics')
      .update({ invitation_sent_at: new Date().toISOString() })
      .eq('id', diagnosticId);

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message || 'Erreur réseau' };
  }
}
